import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";
import { GlobalSearch } from "../components/GlobalSearch";
import { InnovationCopilot } from "../components/InnovationCopilot";
import MoodPopup from "../components/MoodPopup";
import ConfettiBurst from "../components/ConfettiBurst";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Home,
  Briefcase,
  Beaker,
  FolderKanban,
  HandCoins,
  Users,
  UserCog,
  Clock,
  Receipt,
  BarChart3,
  Bell,
  Check,
  AlertTriangle,
  Info,
  FileText,
  CalendarDays,  Sun,
  Moon,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Rocket,
  Sparkles,
  Menu,
  Settings,
  ListTodo,
  ListChecks,
  GanttChart,
  Layers,
  Trophy,
  Shield,
  User,
  DollarSign,
  Download,
  Award,
  Telescope,
  Calendar,
  Wallet,
  Euro,
  Palmtree,
  UserCircle,
  MessageSquare,
  ClipboardCheck,
  FolderOpen,
  BookOpen,
  Gauge,
  Microscope,
  Network,
  Radar,
  ClipboardList,
  Factory,
  Target,
  ShieldCheck,
  Scale,
  ChevronDown,
  Medal,
  Gavel,
  GitMerge,
} from "lucide-react";

const navSections = [
  {
    title: null,
    items: [
      { path: "/", icon: Home, label: "Inicio", exact: true },
      { path: "/dashboard", icon: BarChart3, label: "Panel" },
    ],
  },
  {
    title: "Gestionar",
    items: [
      { path: "/portfolio", icon: Briefcase, label: "Portfolio" },
      { path: "/tareas", icon: ListTodo, label: "Tablero de tareas", exact: true },
      { path: "/tareas/mias", icon: ListChecks, label: "Mis tareas" },
      { path: "/programas", icon: Layers, label: "Programas" },
      { path: "/pilots", icon: Beaker, label: "Pilotos" },
      { path: "/projects", icon: FolderKanban, label: "Balances Proyectos I+D" },
      { path: "/licitaciones", icon: Gavel, label: "Licitaciones I+D" },
      { path: "/production", icon: Factory, label: "Producción" },
      { path: "/planificacion", icon: GanttChart, label: "Planificación" },
      { path: "/focus-day", icon: Target, label: "Focus Day", pmHidden: true },
      { path: "/fireflies/suggestions", icon: Sparkles, label: "Fireflies · Coincidencias", pmHidden: true },
      { path: "/approvals", icon: ClipboardCheck, label: "Aprobaciones" },
      { path: "/propuestas", icon: FileText, label: "Propuestas" },
      { path: "/docs", icon: FolderOpen, label: "Documentación" },
      { path: "/grants", icon: HandCoins, label: "Subvenciones" },
      { path: "/premios", icon: Medal, label: "Premios Innovación" },
      { path: "/patents", icon: Award, label: "Patentes" },
      { path: "/partners", icon: Users, label: "Ecosistema" },
    ],
  },
  {
    title: "Descubrir",
    items: [
      { path: "/nova", icon: Sparkles, label: "NOVA · Retos" },
      { path: "/surveys", icon: ClipboardList, label: "Encuestas", pmHidden: true },
      { path: "/eventos", icon: Calendar, label: "Eventos Innovación" },
      { path: "/app-catalog", icon: Beaker, label: "Catálogo Innovaciones", pmHidden: true },
      { path: "/shakers", icon: Trophy, label: "Shakers", adminOnly: true },
    ],
  },
  {
    title: "Observatorio + Research",
    items: [
      { path: "/observatorio", icon: Telescope, label: "Investigación de Ecosistema" },
      { path: "/vigilancia", icon: Radar, label: "Tendencias" },
      { path: "/dealflow-deals", icon: HandCoins, label: "Dealflow" },
      { path: "/contacts", icon: Users, label: "Contactos" },
      { path: "/interactions", icon: MessageSquare, label: "Interacciones" },
      { path: "/climatetech-accelerators", icon: Sparkles, label: "ClimateTech Accelerators" },
    ],
  },
  {
    title: "Sistema de Gestión I+D+i 56001",
    collapsible: true,
    defaultCollapsed: true,
    icon: ShieldCheck,
    items: [
      { path: "/pg07", icon: ShieldCheck, label: "Cumplimiento PG-07" },
      { path: "/pg07/extras", icon: ClipboardCheck, label: "Fichas & Cierre" },
      { path: "/pg07/cierre-dashboard", icon: Trophy, label: "Panel de Cierre" },
      { path: "/pg07/auditoria", icon: Scale, label: "Auditoría anual" },
      { path: "/auren", icon: Award, label: "Lista de verificación ISO 56001" },
    ],
  },
  {
    title: "Medir",
    items: [
      { path: "/impacto", icon: Target, label: "Impacto" },
      { path: "/xv", icon: Gauge, label: "xV · Valor Esperado", pmHidden: true },
      { path: "/finanzas", icon: Wallet, label: "Finanzas" },
    ],
  },
  {
    title: "Equipo",
    items: [
      { path: "/equipo", icon: Users, label: "Equipo" },
      { path: "/personal", icon: UserCog, label: "Personal", personalAccess: true },
      { path: "/comunicacion", icon: MessageSquare, label: "Chuletario" },
    ],
  },
  {
    title: "Sistema",
    items: [
      { path: "/admin", icon: Shield, label: "Administración", adminOnly: true, exact: true },
      { path: "/admin/cross-link", icon: Network, label: "Cross-Link Airtable", adminOnly: true },
      { path: "/admin/duplicados", icon: GitMerge, label: "Revisión de duplicados", adminOnly: true },
      { path: "/settings", icon: Settings, label: "Configuración" },
    ],
  },
];

export const DashboardLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = localStorage.getItem("nav_collapsed_sections_v1");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return Object.fromEntries(navSections.filter(s => s.collapsible && s.defaultCollapsed).map(s => [s.title, true]));
  });
  const toggleSection = (title) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [title]: !prev[title] };
      try { localStorage.setItem("nav_collapsed_sections_v1", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [alertsList, setAlertsList] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [myTasksAlertCount, setMyTasksAlertCount] = useState(0);

  const refreshAlerts = () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) return;
    api.getAlerts(token, true).then((data) => {
      const list = Array.isArray(data) ? data : [];
      setAlertsList(list);
      setUnreadAlerts(list.length);
    }).catch(() => {});
    // Indicador de bloqueadas/esperando respuesta (spec §tablero §4.7) — solo
    // cuenta lo que afecta directamente a este usuario, no el total global.
    api.getMyTasks(token).then((d) => {
      const blocked = (d.assigned_to_me || []).filter((t) => t.status === "blocked").length;
      setMyTasksAlertCount(blocked + (d.waiting_for_me || []).length + (d.pending_my_approval || []).length);
    }).catch(() => {});
  };

  useEffect(() => { refreshAlerts(); }, [location.pathname]);

  const markAlertRead = async (alertId) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    try {
      await api.markAlertRead(token, alertId);
      setAlertsList((prev) => prev.filter((a) => a.id !== alertId));
      setUnreadAlerts((prev) => Math.max(0, prev - 1));
    } catch {
      // no-op — se reintentará en el próximo refresh
    }
  };

  const severityIcon = (severity) => {
    if (severity === "critical" || severity === "warning") return <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    return <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />;
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const downloadResumenMD = async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/export/resumen-innovacion.md`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const text = await res.text();
      const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "resumen_innovacion_valoriza.md";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error descargando informe:", e);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`sidebar transition-all duration-300 ${
          sidebarCollapsed ? "w-16" : "w-60"
        } ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
          {sidebarCollapsed ? (
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-white text-lg"
              style={{ background: "linear-gradient(135deg, #007934, #3BAD2D)" }}>
              V
            </div>
          ) : (
            <div className="animate-slide-in flex flex-col gap-0.5">
              <img src="/valoriza-logo.svg" alt="Valoriza" className="h-7 w-auto object-contain" />
              <p className="text-[10px] text-slate-400 font-medium tracking-wider uppercase pl-0.5">Innovation OS</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 overflow-y-auto scrollbar-thin">
          {navSections.map((section, si) => {
            const visibleItems = section.items.filter((item) => {
              if (item.adminOnly && user?.role !== 'admin') return false;
              if (item.restricted && !item.restricted.includes(user?.email)) return false;
              if (item.personalAccess && user?.role !== 'admin' && (user?.email || '').toLowerCase() !== 'pmingo@valorizasm.com') return false;
              // Fix 2026-08-28: vuelto a oculto GLOBAL (el 28/08 se probó por rol,
              // pero el usuario quiere su propio sidebar también reducido) —
              // accesible solo por URL directa.
              if (item.pmHidden) return false;
              return true;
            });
            if (!visibleItems.length) return null;
            const isCollapsed = section.collapsible && collapsedSections[section.title];
            const hasActiveChild = section.collapsible && visibleItems.some(item => (item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)));
            const showChildren = !isCollapsed || hasActiveChild;
            const SectionIcon = section.icon;
            return (
              <div key={si} className="mb-1">
                {section.title && !sidebarCollapsed && (
                  section.collapsible ? (
                    <button
                      onClick={() => toggleSection(section.title)}
                      className="w-full px-3 pt-3 pb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-slate-300 transition-colors"
                      data-testid={`nav-section-toggle-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {SectionIcon && <SectionIcon className="w-3 h-3 flex-shrink-0" />}
                      <span className="flex-1 text-left truncate">{section.title}</span>
                      <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isCollapsed && !hasActiveChild ? "-rotate-90" : ""}`} />
                    </button>
                  ) : (
                    <p className="px-3 pt-3 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {section.title}
                    </p>
                  )
                )}
                {section.title && sidebarCollapsed && (
                  <div className="mx-3 my-2 border-t border-white/10" />
                )}
                {showChildren && (
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const isActive = item.exact
                        ? location.pathname === item.path
                        : location.pathname.startsWith(item.path);
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={!!item.exact}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`sidebar-item ${isActive ? "active" : ""}`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          title={item.label}
                        >
                          <item.icon className="w-5 h-5 flex-shrink-0" />
                          {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Collapse button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden md:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-slate-700 border border-slate-600 items-center justify-center text-slate-300 hover:bg-slate-600 transition-colors"
          data-testid="sidebar-toggle"
          aria-label={sidebarCollapsed ? "Expandir menú lateral" : "Colapsar menú lateral"}
          title={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <main
        className={`transition-all duration-300 ${
          sidebarCollapsed ? "md:ml-16" : "md:ml-60"
        }`}
      >
        {/* Topbar */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-b border-border/40 px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Abrir menú de navegación"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Search */}
            <div className="flex-1 max-w-xl">
              <GlobalSearch />
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-muted-foreground hover:text-foreground"
                data-testid="theme-toggle"
                aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
                title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>

              {/* Tablero de tareas: bloqueadas / esperando mi respuesta */}
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground relative"
                data-testid="tasks-alert-btn"
                aria-label={myTasksAlertCount > 0 ? `${myTasksAlertCount} tareas necesitan tu atención` : "Tareas"}
                title="Tareas que necesitan tu atención"
                onClick={() => navigate("/tareas/mias")}
              >
                <ListChecks className="w-5 h-5" />
                {myTasksAlertCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-semibold bg-red-500 text-white rounded-full" data-testid="tasks-alert-count">
                    {myTasksAlertCount}
                  </span>
                )}
              </Button>

              {/* Notifications */}
              <Popover open={alertsOpen} onOpenChange={setAlertsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground relative"
                    data-testid="alerts-btn"
                    aria-label={unreadAlerts > 0 ? `${unreadAlerts} alertas sin leer` : "Alertas"}
                    title="Alertas"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadAlerts > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" data-testid="alerts-unread-dot" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0" data-testid="alerts-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <p className="font-semibold text-sm">Notificaciones</p>
                    {unreadAlerts > 0 && <span className="text-xs text-muted-foreground">{unreadAlerts} sin leer</span>}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {alertsList.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground" data-testid="alerts-panel-empty">
                        Sin alertas pendientes
                      </div>
                    ) : (
                      alertsList.slice(0, 8).map((a) => (
                        <div key={a.id} className="flex items-start gap-2 px-4 py-3 border-b last:border-b-0 hover:bg-muted/50" data-testid={`alert-item-${a.id}`}>
                          {severityIcon(a.severity)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium leading-tight">{a.title}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{a.message}</p>
                          </div>
                          <button
                            onClick={() => markAlertRead(a.id)}
                            className="text-muted-foreground hover:text-foreground flex-shrink-0 p-1 rounded hover:bg-muted"
                            title="Marcar como leída"
                            aria-label="Marcar como leída"
                            data-testid={`alert-mark-read-${a.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <button
                    onClick={() => { setAlertsOpen(false); navigate("/alerts"); }}
                    className="w-full text-center text-sm text-primary py-2.5 border-t hover:bg-muted/50"
                    data-testid="alerts-panel-view-all"
                  >
                    Ver todas las alertas
                  </button>
                </PopoverContent>
              </Popover>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-2"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    {!sidebarCollapsed && (
                      <span className="hidden md:block text-sm font-medium max-w-[120px] truncate">
                        {user?.full_name}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.full_name}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        {user?.email}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/profile")}
                    className="cursor-pointer"
                    data-testid="profile-menu-item"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Mi Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-xs text-muted-foreground">
                    Rol: {user?.role?.replace("_", " ")}
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={downloadResumenMD}
                        className="cursor-pointer text-emerald-600 dark:text-emerald-400 focus:text-emerald-600"
                        data-testid="download-resumen-md"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Descargar informe .md
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-destructive focus:text-destructive cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>

        {/* Innovation Copilot */}
        <InnovationCopilot />
        
        {/* Daily Mood Popup */}
        <MoodPopup />

        {/* Global confetti overlay */}
        <ConfettiBurst />
      </main>
    </div>
  );
};
